import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  filterBy,
  CompositeFilterDescriptor,
} from "@progress/kendo-data-query";
import {
  Grid as KendoGrid,
  GridColumn as Column,
  GridCellProps,
  GridFilterChangeEvent,
  GridPageChangeEvent,
  GridCustomHeaderCellProps,
  GridCustomCellProps,
} from "@progress/kendo-react-grid";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { PagerTargetEvent } from "@progress/kendo-react-data-tools";
import { HeaderThElement } from "@progress/kendo-react-data-tools";
import { Typography } from "@progress/kendo-react-common";
import { Button } from "@progress/kendo-react-buttons";
import { FiEdit } from "react-icons/fi";
import { TextBox } from "@progress/kendo-react-inputs";
import AlertBox from "../../components/common/AlertBox";
import { LoadingPanel } from "../../components/layout/Loading";
import IconButton from "../../components/common/IconButton";
import { BiSolidDuplicate } from "react-icons/bi";
import AppDialog, { DialogCloseButton } from "../../components/dialog/Dialog";
import { closeDialog, openDialog } from "../../components/dialog/dialogSlice";
import { MdDelete } from "react-icons/md";
import {
  deleteWhatsappAutoTrigger,
  duplicateWhatsappAutoTrigger,
  getAllWhatsappAutoTriggers,
} from "./services/whatsappAutoTrigger.services";
import {
  clearWhatsappAutoTriggerID,
  setFilterWhatsappAutoTrigger,
  setWhatsappAutoTriggerID,
} from "./whatsappAutoTriggerSlice";
import { checkAcessRights } from "../../_helper/helper";
import {
  DELETE_ACCESS,
  EDIT_ACCESS,
  INSERT_ACCESS,
} from "../../_contstants/common";
import { filterClearIcon } from "@progress/kendo-svg-icons";
import _debounce from "lodash/debounce";

const WhatsappAutoTrigger: React.FC = () => {
  const navigate = useNavigate();

  const location = useLocation();
  const isAddAccess = checkAcessRights(location.pathname, INSERT_ACCESS);

  const handleCreate = () => {
    navigate("/autotrigger/whatsapp/create");
  };

  return (
    <>
      <DeleteWhatsappAutoTriggerDialog />
      <GridLayout cols={[{ width: "100%" }]}>
        <GridLayoutItem
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "4px 0px 20px",
          }}
        >
          <Typography.h4 style={{ marginBottom: 0, marginLeft: "3px" }}>
            WhatsApp Automation Trigger List
          </Typography.h4>
          {isAddAccess && (
            <Button
              onClick={handleCreate}
              fillMode={"solid"}
              themeColor={"primary"}
            >
              {"Add New"}
            </Button>
          )}
        </GridLayoutItem>
        <GridLayoutItem>
          <WhatsappAutoTriggerGridComponent />
        </GridLayoutItem>
      </GridLayout>
    </>
  );
};

interface PageState {
  skip: number;
  take: number;
}

const initialDataState: PageState = { skip: 0, take: 10 };

const WhatsappAutoTriggerGridComponent: React.FC = () => {
  const gridRef = useRef<any>(null);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const WhatsappAutoTriggerList = useAppSelector(
    (state) => state.whatsappAutoTrigger.WhatsappAutoTriggerList
  );
  const loading = useAppSelector((state) => state.whatsappAutoTrigger.loading);
  const location = useLocation();
  const isAddAccess = checkAcessRights(location.pathname, INSERT_ACCESS);
  const isEditAccess = checkAcessRights(location.pathname, EDIT_ACCESS);
  const isDeleteAccess = checkAcessRights(location.pathname, DELETE_ACCESS);
  const FilterWhatsappAutoTrigger: any = useAppSelector(
    (state) => state.whatsappAutoTrigger.FilterWhatsappAutoTrigger
  );

  useEffect(() => {
    dispatch(getAllWhatsappAutoTriggers({ ...FilterWhatsappAutoTrigger }));

    return () => {
      dispatch(closeDialog());
    };
  }, []);

  const [filter, setFilter] = React.useState<
    CompositeFilterDescriptor | undefined
  >(undefined);
  const [page, setPage] = React.useState<PageState>(initialDataState);
  const [pageSizeValue, setPageSizeValue] = React.useState<
    number | string | undefined
  >();

  const pageChange = (event: GridPageChangeEvent) => {
    const targetEvent = event.targetEvent as PagerTargetEvent;
    const take =
      targetEvent.value === "All"
        ? WhatsappAutoTriggerList.length
        : event.page.take;

    if (targetEvent.value) {
      setPageSizeValue(targetEvent.value);
    }
    setPage({
      ...event.page,
      take,
    });
  };

  const MyEditCommandCell = (props: GridCellProps) => {
    const handleEdit = (ID: number) => {
      navigate("/autotrigger/whatsapp/edit", {
        state: { WhatsappAutoTriggerID: ID },
      });
    };

    const handleOpenDeleteDialog = (ID: number) => {
      dispatch(openDialog("deleteWhatsappAutoTrigger"));
      dispatch(setWhatsappAutoTriggerID(ID));
    };

    const handleDuplicateTrigger = async (ID: number) => {
      if (ID) {
        const res = await dispatch(duplicateWhatsappAutoTrigger(ID));
        if (res.meta?.requestStatus === "fulfilled") {
          dispatch(
            getAllWhatsappAutoTriggers({ ...FilterWhatsappAutoTrigger })
          );
        }
      }
    };

    return (
      <td
        className="action-td"
        style={{
          ...props.style,
        }}
      >
        {isEditAccess && (
          <IconButton
            type="button"
            fillMode="flat"
            size="small"
            title="Edit"
            onClick={() => handleEdit(props.dataItem.ID)}
            icon={FiEdit}
            iconClassName="absolute-position"
            iconStyle={{ fontSize: "20px" }}
          />
        )}
        {isDeleteAccess && (
          <IconButton
            type="button"
            fillMode="flat"
            size="small"
            title="Delete"
            className="color-red"
            onClick={() => handleOpenDeleteDialog(props.dataItem.ID)}
            icon={MdDelete}
            iconClassName="absolute-position"
            iconStyle={{ fontSize: "24px" }}
          />
        )}
        {isAddAccess && (
          <IconButton
            type="button"
            fillMode="flat"
            size="small"
            title="Duplicate Trigger"
            themeColor="success"
            onClick={() => handleDuplicateTrigger(props.dataItem.ID)}
            icon={BiSolidDuplicate}
            iconClassName="absolute-position"
            iconStyle={{ fontSize: "24px" }}
          />
        )}
      </td>
    );
  };

  const handleFilterChange = (e: GridFilterChangeEvent) => {
    setFilter(e.filter);
    setPage({ skip: 0, take: page.take });
  };
  const HeaderCustomCell = (props: GridCustomHeaderCellProps) => (
    <HeaderThElement
      columnId={props.thProps?.columnId || ""}
      {...props.thProps}
      className="table-header"
    >
      {props.children}
    </HeaderThElement>
  );

  const CustomCell = (props: any) => {
    return (
      <td {...props.tdProps} colSpan={1} className={"table-cell"}>
        {props.children}
      </td>
    );
  };

  const MyDataCustomCell = (props: GridCustomCellProps) => (
    <CustomCell {...props} />
  );

  return (
    <>
      {loading && <LoadingPanel gridRef={gridRef} />}
      {WhatsappAutoTriggerList && WhatsappAutoTriggerList.length ? (
        <KendoGrid
          style={{ maxHeight: "calc(100vh - 152px)" }}
          className="responsive-table"
          filterable={true}
          filter={filter}
          data={
            filter
              ? filterBy(WhatsappAutoTriggerList, filter).slice(
                  page.skip,
                  page.take + page.skip
                )
              : WhatsappAutoTriggerList.slice(page.skip, page.take + page.skip)
          }
          onFilterChange={handleFilterChange}
          skip={page.skip}
          take={page.take}
          total={WhatsappAutoTriggerList.length}
          pageable={{
            buttonCount: 5,
            pageSizes: [5, 10, 15, "All"],
            pageSizeValue: pageSizeValue,
            type: "input",
          }}
          onPageChange={pageChange}
          cells={{
            headerCell: HeaderCustomCell,
            data: MyDataCustomCell,
          }}
        >
          <Column
            field="TriggerName"
            title="Trigger Name"
            cells={{
              data: (props: any) => (
                <td>
                  {isEditAccess ? (
                    <div
                      className="hoverableName"
                      onClick={() =>
                        navigate("/autotrigger/whatsapp/edit", {
                          state: {
                            WhatsappAutoTriggerID: props.dataItem?.ID,
                          },
                        })
                      }
                    >
                      {props.dataItem?.TriggerName}
                    </div>
                  ) : (
                    <div className="normalName">
                      {props.dataItem?.TriggerName}
                    </div>
                  )}
                </td>
              ),
              filterCell: CommonFilterCell,
            }}
          />
          <Column
            field="Module"
            title="Module"
            cells={{
              data: (props: any) => <td>{props.dataItem?.Module}</td>,
              filterCell: CommonFilterCell,
            }}
          />
          <Column
            field="Operation"
            title="Operation"
            cells={{
              data: (props: any) => <td>{props.dataItem?.Operation}</td>,
              filterCell: CommonFilterCell,
            }}
          />
          <Column
            field="Whats_App_To"
            title="Trigger To"
            cells={{
              data: (props: any) => <td>{props.dataItem?.Whats_App_To}</td>,
              filterCell: CommonFilterCell,
            }}
          />
          <Column
            field="DelayDay"
            title="Delay"
            cells={{
              data: (props: any) => (
                <td>
                  {props.dataItem?.DelayDay
                    ? `${props.dataItem?.DelayDay} ${
                        props.dataItem?.DelayDay > 1 ? "Days" : "Day"
                      }`
                    : ""}
                </td>
              ),
            }}
          />
          {(isDeleteAccess || isEditAccess || isAddAccess) && (
            <Column
              field="ID"
              title="Actions"
              cells={{ data: MyEditCommandCell }}
              width={"140px"}
              filterable={false}
            />
          )}
        </KendoGrid>
      ) : (
        <AlertBox />
      )}
    </>
  );
};

const DeleteWhatsappAutoTriggerDialog: React.FC = () => {
  const dispatch = useAppDispatch();
  const WhatsappAutoTriggerID = useAppSelector(
    (state) => state.whatsappAutoTrigger.WhatsappAutoTriggerID
  );
  const dialogName = useAppSelector((state) => state.dialog.dialogName);
  const FilterWhatsappAutoTrigger: any = useAppSelector(
    (state) => state.whatsappAutoTrigger.FilterWhatsappAutoTrigger
  );

  const handleDeleteAction = async (ID: number | null) => {
    if (ID) {
      const response = await dispatch(deleteWhatsappAutoTrigger(ID));
      if (response.meta.requestStatus === "fulfilled") {
        dispatch(getAllWhatsappAutoTriggers({ ...FilterWhatsappAutoTrigger }));
        dispatch(closeDialog());
        dispatch(clearWhatsappAutoTriggerID());
      } else {
        dispatch(getAllWhatsappAutoTriggers({ ...FilterWhatsappAutoTrigger }));
        dispatch(closeDialog());
        dispatch(clearWhatsappAutoTriggerID());
      }
    }
  };

  return (
    <>
      {dialogName === "deleteWhatsappAutoTrigger" && (
        <AppDialog>
          <>
            <Typography.h5>
              {"Delete Whatsapp Automation Trigger"}
            </Typography.h5>
            <GridLayout>
              <GridLayoutItem>
                <Typography.p>
                  Are you sure you want to delete this Whatsapp Automation
                  Trigger ?
                </Typography.p>
              </GridLayoutItem>
              <GridLayoutItem
                style={{
                  display: "flex",
                  justifyContent: "end",
                }}
              >
                <DialogCloseButton themeColor="error" />
                <Button
                  fillMode={"solid"}
                  themeColor={"error"}
                  onClick={() => handleDeleteAction(WhatsappAutoTriggerID)}
                  type="button"
                >
                  Delete
                </Button>
              </GridLayoutItem>
            </GridLayout>
          </>
        </AppDialog>
      )}
    </>
  );
};

const CommonFilterCell = (props: any) => {
  const dispatch = useAppDispatch();
  const FilterWhatsappAutoTrigger: any = useAppSelector(
    (state) => state.whatsappAutoTrigger.FilterWhatsappAutoTrigger
  );

  const isFilterRef = useRef(true);
  const [inputValue, setInputValue] = useState(props.value || "");
  const [inputField, setInputField] = useState(props.field || "");

  const onChange = (event: any) => {
    setInputValue(event.target.value);
    setInputField(event.target.name);
  };

  const delayedApiCall = React.useCallback(
    _debounce((updatedFilters: any) => {
      const payload = {
        ...updatedFilters,
      };

      dispatch(getAllWhatsappAutoTriggers(payload));
    }, 300),
    [dispatch]
  );

  useEffect(() => {
    if (!isFilterRef.current) {
      const updatedFilters = {
        ...FilterWhatsappAutoTrigger,
        [inputField]: inputValue,
      };
      dispatch(setFilterWhatsappAutoTrigger(updatedFilters));
      delayedApiCall(updatedFilters);
    } else {
      isFilterRef.current = false;
    }
    return () => {
      delayedApiCall.cancel();
    };
  }, [inputValue, delayedApiCall]);

  const onClearButtonClick = (event: any) => {
    event.preventDefault();
    props.onChange({
      value: null,
      operator: "",
      syntheticEvent: event,
    });

    const updatedFilters = { ...FilterWhatsappAutoTrigger, [props.field]: "" };
    dispatch(setFilterWhatsappAutoTrigger(updatedFilters));

    dispatch(getAllWhatsappAutoTriggers({ ...updatedFilters }));
  };

  return (
    <td>
      <div className="d-flex">
        <TextBox
          value={FilterWhatsappAutoTrigger[props.field] || ""}
          name={props.field || ""}
          onChange={onChange}
        />
        <Button
          svgIcon={filterClearIcon}
          title="Clear"
          disabled={!FilterWhatsappAutoTrigger[props.field]}
          onClick={onClearButtonClick}
          type="button"
          style={{
            marginLeft: "8px",
          }}
        ></Button>
      </div>
    </td>
  );
};

export default WhatsappAutoTrigger;
